import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPause, faVolumeMute, faVolumeUp, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { Subject, distinctUntilKeyChanged, filter, take, tap } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { UplayButtonComponent } from '@usheru-hff/core/uplay-button';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieService } from '@usheru-hff/sdk/movie';
import { AnyVideo, getMovieGenres, getMovieTags, Movie, TabType } from '@usheru-hff/shared/domain';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { VideoComponent, VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { CookiesSettingsType, SharedUtilsCookiesModule } from '@usheru-hff/shared/utils-cookies';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { ExtraRouteData } from '@usheru-hff/shared/utils-navigation';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { SharedUtilsTranslationModule } from '@usheru-hff/shared/utils-translation';

import { LogoSizes } from './logo-sizes.interfaces';

@Component({
  selector: 'ush-feature-movie-hero',
  standalone: true,
  imports: [
    UplayButtonComponent,
    CommonModule,
    VideoComponent,
    SharedUtilsTranslationModule,
    ButtonModule,
    FontAwesomeModule,
    SharedUtilsCookiesModule,
    SharedUtilsImageModule,
    LoadingIndicatorComponent
  ],
  templateUrl: './feature-movie-hero.component.html',
  styleUrl: './feature-movie-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureMovieHeroComponent implements OnInit {
  private env = inject(ENVIRONMENT) as Environment;
  private route = inject(ActivatedRoute);
  private fullScreenService = inject(VideoFullscreenService);
  private channelService = inject(ChannelService);
  private showtimesStreamingService = inject(ShowtimeStreamingService);
  private trackingService = inject(TrackingService);
  private cd = inject(ChangeDetectorRef);

  readonly trackingCategory = 'MovieDetail';

  readonly cookiesRequired: CookiesSettingsType[] = ['functionality_storage'];

  @ViewChild('video') videoComponent?: VideoComponent;

  @Input() disableTrailer = false;
  movie = input<Movie>();
  @Input() disableMovieInfo = false;
  @Input() customMovieLogo: string | undefined;
  @Input() loading = false;

  @Input() set logoSizes(value: LogoSizes | undefined) {
    this.setLogoSizes(value);
  }

  @Input({ transform: booleanAttribute }) hideWatchNowCtaButton!: boolean;

  playing = signal<boolean>(false);
  seeMoreEnabled = signal<boolean>(false);
  muteIcon = faVolumeUp;
  unmuteIcon = faVolumeMute;
  pauseIcon = faPause;
  playIcon = faPlay;

  embed = signal<boolean>(false);
  autoplay = signal<boolean>(false);
  muted = signal<boolean>(false);
  embedVideoControls = signal<boolean>(false);

  titleSig = computed(() => this.movie()?.title);
  releaseMessageSig = computed(() => this.movie()?.releaseMessage);
  hasTrailerSig = computed(() => !!this.movie()?.trailerLink);
  heroImageSig = computed(() => this.getHeroImageFromMovie(this.movie()));
  hasUplayerSig = computed(() => !!this.movie()?.hasPlayer);
  openTab!: TabType;

  /**
   * This is required because the logo is rendered one and the sizes could be set as an input
   */
  // logoSizesReady = signal(false);
  logoSizesDefault: LogoSizes = {
    width: 450,
    height: 150,
    sizes: '(max-width:530px) 90vw, (max-width:1000px) 50vw, 30vw',
    style: 'height: auto; max-width: 50vw;'
  };

  _logoSizes: LogoSizes | undefined;
  get logoSizes(): LogoSizes | undefined {
    return this._logoSizes;
  }

  //

  enableFullVideo = signal(false);
  additionalLink = signal('');

  destroy$ = new Subject();

  constructor() {
    this.setLogoSizes();

    // If the movie changes for no reason multiple times, that will cause performance issues.
    effect(
      () => {
        const movie = this.movie();
        if (movie) {
          this.setUpVariables(movie);
        }
      },
      {
        allowSignalWrites: true
      }
    );
  }

  ngOnInit(): void {
    this.checkEnableFullVideo();
    this.checkAdditionalLink();
    this.seeMoreEnabled.set(!!this.env.customPageParams?.seeMoreType);
  }

  setUpVariables(movie: Movie) {
    this.embed.set(this.isEmbedEnabled());
    this.autoplay.set(this.isAutoplayEnabled());
    this.embedVideoControls.set(this.autoplay() && this.embed());
    this.setUpCtaMessage(movie);
  }

  getHeroImageFromMovie(movie: Movie | undefined): string {
    if (!movie) return '';
    return movie.tilePhotoUrl || movie.fullpageImageUrl || this.placeholderImg || '';
  }

  private setUpCtaMessage(movie: Movie): void {
    this.showtimesStreamingService
      .determineShowtimeStreamingActiveType(movie)
      .pipe(take(1))
      .subscribe(type => {
        this.openTab = type;
        this.cd.markForCheck();
      });
  }

  private checkEnableFullVideo() {
    const fullVideoParam = (this.route.snapshot.data as ExtraRouteData).fullVideo;
    if (this.env.movieDetail?.hero?.fullVideo && fullVideoParam) {
      this.enableFullVideo.set(true);
    }
  }

  private checkAdditionalLink() {
    const additionalLink = (this.route.snapshot.data as ExtraRouteData).additionalLink;
    this.additionalLink.set(additionalLink);
  }

  private isEmbedEnabled(): boolean {
    return this.env?.movieDetail?.hero?.embed || this.env.customPageParams?.movieDetailVideoEmbed || false;
  }
  private isAutoplayEnabled() {
    return this.env.movieDetail?.hero?.autoplay || false;
  }

  get placeholderImg(): string {
    return this.channelService.getDefaultImg('landscape');
  }

  get logoUrl(): string | undefined {
    return this.customMovieLogo || this.movie()?.logoUrl;
  }

  get ctaButtonLabel(): string {
    if (!this.openTab || this.hideWatchNowCtaButton) return '';

    return this.openTab === 'in-cinemas'
      ? $localize`:@@movie.cta.inCinemas:Book now`
      : $localize`:@@movie.cta.watchAtHome:Watch now`;
  }

  onPlayTrailerClick(): void {
    this.playVideo();
  }

  onPlayFullVideoClick(): void {
    this.playFullscreenVideo('fullvideo');
    const movie = this.movie();
    if (!movie) return;
    this.trackingService.trackEvent({
      action: 'FullVideo',
      properties: {
        category: 'MovieDetail',
        movie: movie.title || '',
        idMovie: movie.id,
        imdb: movie?.imdb,
        tagsMovie: getMovieTags(movie),
        genresMovie: getMovieGenres(movie)
      }
    });
  }

  onPauseButtonClick() {
    this.videoComponent?.pause();
    this.embedVideoControls.set(false);
  }

  muteVideo(): void {
    this.videoComponent?.setMute();
    this.muted.set(true);
  }

  unmuteVideo(): void {
    this.videoComponent?.setUnmute();
    this.muted.set(false);
  }

  onCtaClick(): void {
    if (this.openTab) {
      const el = document.getElementById('showtimeStreamingWidget');
      // scroll to the element with some offset
      if (el) {
        const offset = 100;
        const elementPosition = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: elementPosition, behavior: 'smooth' });
      }
    }
  }

  private playVideo(): void {
    if (this.embed()) {
      this.playEmbedVideo();
    } else {
      this.playFullscreenVideo();
    }
  }

  private playEmbedVideo(): void {
    this.playing.set(true);
    this.embedVideoControls.set(true);
    if (this.videoComponent) {
      this.videoComponent?.reanude();
    } else {
      const videoReadyInterval = setInterval(() => {
        if (this.videoComponent) {
          this.videoComponent?.reanude();
          clearInterval(videoReadyInterval);
        }
      }, 200);
    }
  }

  onEmbedVideoReady(): void {
    if (this.playing()) {
      this.playEmbedVideo();
    }
  }

  private playFullscreenVideo(type: 'trailer' | 'fullvideo' = 'trailer'): void {
    if (type === 'trailer' && this.movie()?.trailerLink) {
      this.fullScreenService.play({
        content: this.movie(),
        category: this.trackingCategory,
        animate: true,
        videoSettings: { muted: false, autoplay: true }
      });
    }
    if (type === 'fullvideo' && this.movie()?.fullVideoUrl) {
      this.fullScreenService.play({
        content: {
          title: this.movie()?.title,
          url: this.movie()?.fullVideoUrl
        } as AnyVideo,
        category: this.trackingCategory,
        animate: true,
        videoSettings: { muted: false, autoplay: true }
      });
    }
  }

  private setLogoSizes(value?: LogoSizes): void {
    const { width, height, sizes, style } = value || {};

    this._logoSizes = {
      width: width || this.logoSizesDefault.width,
      height: height || this.logoSizesDefault.height,
      sizes: sizes || this.logoSizesDefault.sizes,
      style: style || this.logoSizesDefault.style
    };
  }
}
